import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../config";
import { useNavigate } from "react-router-dom";
import {
    ShoppingBag, MapPin, CreditCard, Smartphone, Building, Wallet,
    Check, ChevronRight, ArrowLeft, Copy, QrCode, Clock, Shield,
    CheckCircle, Package, Truck
} from "lucide-react";

const Checkout = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: ข้อมูลจัดส่ง, 2: ชำระเงิน, 3: เสร็จสิ้น
    const [cartItems, setCartItems] = useState([]);
    const [addresses, setAddresses] = useState([]);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);

    // Payment simulation states
    const [cardNumber, setCardNumber] = useState("");
    const [cardExpiry, setCardExpiry] = useState("");
    const [cardCvv, setCardCvv] = useState("");
    const [qrCountdown, setQrCountdown] = useState(300); // 5 minutes
    const [copied, setCopied] = useState(false);
    const [discountCode, setDiscountCode] = useState(""); // สำหรับ bypass
    const [discountApplied, setDiscountApplied] = useState(false);
    const [discountPercent, setDiscountPercent] = useState(0);

    useEffect(() => {
        fetchData();
    }, []);

    // QR Code countdown
    useEffect(() => {
        if (paymentMethod === "promptpay" && step === 2 && qrCountdown > 0) {
            const timer = setInterval(() => {
                setQrCountdown(prev => prev - 1);
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [paymentMethod, step, qrCountdown]);

    const fetchData = async () => {
        try {
            const token = localStorage.getItem("token");

            // Fetch cart items
            const cartRes = await fetch(`${API_BASE_URL}/api/cart/items`, {
                headers: { Authorization: token },
            });
            if (cartRes.ok) {
                const cartData = await cartRes.json();
                setCartItems(cartData);
            }

            // Fetch addresses
            const addrRes = await fetch(`${API_BASE_URL}/api/addresses`, {
                headers: { Authorization: token },
            });
            if (addrRes.ok) {
                const addrData = await addrRes.json();
                setAddresses(addrData);
                // Auto-select default address
                const defaultAddr = addrData.find(a => a.isDefault);
                if (defaultAddr) setSelectedAddress(defaultAddr.addressId);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const calculateSubtotal = () => {
        return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    };

    const calculateDiscount = () => {
        if (!discountApplied) return 0;
        return Math.floor(calculateSubtotal() * (discountPercent / 100));
    };

    const calculateTotal = () => {
        return calculateSubtotal() - calculateDiscount();
    };

    const applyDiscountCode = () => {
        const code = discountCode.trim().toUpperCase();
        if (code === "BYPASS") {
            setDiscountApplied(true);
            setDiscountPercent(100);
        } else {
            alert("รหัสส่วนลดไม่ถูกต้อง");
            setDiscountApplied(false);
            setDiscountPercent(0);
        }
    };

    const removeDiscount = () => {
        setDiscountApplied(false);
        setDiscountPercent(0);
        setDiscountCode("");
    };

    const handleConfirmPayment = async () => {
        // ต้องกรอกโค้ด BYPASS ก่อนถึงจะชำระเงินได้
        if (!discountApplied || discountCode.trim().toUpperCase() !== "BYPASS") {
            alert("กรุณากรอกโค้ด BYPASS เพื่อทดสอบการชำระเงิน");
            return;
        }

        setProcessing(true);

        // Simulate payment processing
        await new Promise(resolve => setTimeout(resolve, 2000));



        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/orders/checkout`, {
                method: "POST",
                headers: {
                    "Authorization": token,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(cartItems),
            });

            if (response.ok) {
                setStep(3);
            } else {
                alert("การสั่งซื้อล้มเหลว กรุณาลองใหม่");
            }
        } catch (error) {
            console.error("Checkout error:", error);
            alert("เกิดข้อผิดพลาด กรุณาลองใหม่");
        } finally {
            setProcessing(false);
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const paymentMethods = [
        { id: "promptpay", name: "พร้อมเพย์ / QR Code", icon: QrCode, color: "blue" },
        { id: "credit", name: "บัตรเครดิต / เดบิต", icon: CreditCard, color: "purple" },
        { id: "mobile", name: "Mobile Banking", icon: Smartphone, color: "green" },
        { id: "transfer", name: "โอนเงินธนาคาร", icon: Building, color: "orange" },
        { id: "wallet", name: "TrueMoney Wallet", icon: Wallet, color: "red" },
    ];

    // Loading State
    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="text-center">
                    <div className="w-10 h-10 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-white/60">กำลังดำเนินการ...</p>
                </div>
            </div>
        );
    }

    // Success State
    if (step === 3) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center p-4">
                <div className="max-w-md w-full text-center">
                    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-10 animate-scale-in">
                        <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce-slow">
                            <CheckCircle className="w-12 h-12 text-green-400" />
                        </div>
                        <h1 className="text-3xl font-bold text-white mb-2">สั่งซื้อสำเร็จ!</h1>
                        <p className="text-white/60 mb-8">ขอบคุณสำหรับการสั่งซื้อ เราได้รับคำสั่งซื้อของคุณแล้ว</p>

                        <div className="bg-white/5 rounded-xl p-6 mb-8 text-left space-y-4">
                            <div className="flex items-center gap-3 text-white/80">
                                <Package className="w-5 h-5 text-red-500" />
                                <span>กำลังเตรียมสินค้า</span>
                            </div>
                            <div className="flex items-center gap-3 text-white/40">
                                <Truck className="w-5 h-5" />
                                <span>รอการจัดส่ง</span>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => navigate("/account/orders")}
                                className="flex-1 py-3 px-6 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20 transition-all"
                            >
                                ดูรายการคำสั่งซื้อ
                            </button>
                            <button
                                onClick={() => navigate("/all-products")}
                                className="flex-1 py-3 px-6 bg-red-600 text-white rounded-xl font-medium hover:bg-red-500 transition-all"
                            >
                                กลับหน้าแรก
                            </button>
                        </div>
                    </div>
                </div>

                <style jsx>{`
          @keyframes scale-in {
            from { opacity: 0; transform: scale(0.9); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes bounce-slow {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }
          .animate-scale-in { animation: scale-in 0.5s ease-out; }
          .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }
        `}</style>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Header */}
            <div className="bg-black text-white py-6">
                <div className="max-w-4xl mx-auto px-4">
                    <button
                        onClick={() => step === 1 ? navigate("/my-cart") : setStep(1)}
                        className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-4"
                    >
                        <ArrowLeft size={20} />
                        {step === 1 ? "กลับไปตะกร้าสินค้า" : "ย้อนกลับ"}
                    </button>
                    <h1 className="text-2xl font-bold text-white">ชำระเงิน</h1>

                    {/* Steps */}
                    <div className="flex items-center gap-4 mt-6">
                        <div className={`flex items-center gap-2 ${step >= 1 ? "text-white" : "text-white/40"}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? "bg-red-600" : "bg-white/20"}`}>
                                {step > 1 ? <Check size={16} /> : "1"}
                            </div>
                            <span className="hidden sm:inline">ข้อมูลจัดส่ง</span>
                        </div>
                        <ChevronRight className="text-white/40" size={20} />
                        <div className={`flex items-center gap-2 ${step >= 2 ? "text-white" : "text-white/40"}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? "bg-red-600" : "bg-white/20"}`}>
                                {step > 2 ? <Check size={16} /> : "2"}
                            </div>
                            <span className="hidden sm:inline">ชำระเงิน</span>
                        </div>
                        <ChevronRight className="text-white/40" size={20} />
                        <div className={`flex items-center gap-2 ${step >= 3 ? "text-white" : "text-white/40"}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 3 ? "bg-red-600" : "bg-white/20"}`}>
                                3
                            </div>
                            <span className="hidden sm:inline">เสร็จสิ้น</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Step 1: Address Selection */}
                        {step === 1 && (
                            <div className="bg-white rounded-xl shadow-sm p-6">
                                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <MapPin className="w-5 h-5 text-red-600" />
                                    ที่อยู่สำหรับจัดส่ง
                                </h2>

                                {addresses.length === 0 ? (
                                    <div className="text-center py-10">
                                        <MapPin className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                                        <p className="text-gray-600 mb-4">คุณยังไม่มีที่อยู่จัดส่ง</p>
                                        <button
                                            onClick={() => navigate("/account/profile")}
                                            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                        >
                                            เพิ่มที่อยู่ใหม่
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {addresses.map((addr) => (
                                            <label
                                                key={addr.addressId}
                                                className={`block p-4 border-2 rounded-xl cursor-pointer transition-all ${selectedAddress === addr.addressId
                                                    ? "border-red-500 bg-red-50"
                                                    : "border-gray-200 hover:border-gray-300"
                                                    }`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <input
                                                        type="radio"
                                                        name="address"
                                                        checked={selectedAddress === addr.addressId}
                                                        onChange={() => setSelectedAddress(addr.addressId)}
                                                        className="mt-1 w-4 h-4 text-red-600 focus:ring-red-500"
                                                    />
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="font-medium text-gray-900">{addr.recipientName}</span>
                                                            {addr.isDefault && (
                                                                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">ค่าเริ่มต้น</span>
                                                            )}
                                                        </div>
                                                        <p className="text-gray-600 text-sm">{addr.phone}</p>
                                                        <p className="text-gray-600 text-sm">
                                                            {addr.addressLine}, {addr.district}, {addr.province} {addr.postalCode}
                                                        </p>
                                                    </div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}

                                <button
                                    onClick={() => setStep(2)}
                                    disabled={!selectedAddress}
                                    className="w-full mt-6 py-4 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    ดำเนินการต่อ
                                    <ChevronRight size={20} />
                                </button>
                            </div>
                        )}

                        {/* Step 2: Payment */}
                        {step === 2 && (
                            <>
                                {/* Payment Method Selection */}
                                <div className="bg-white rounded-xl shadow-sm p-6">
                                    <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                        <CreditCard className="w-5 h-5 text-red-600" />
                                        เลือกวิธีการชำระเงิน
                                    </h2>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        {paymentMethods.map((method) => {
                                            const Icon = method.icon;
                                            return (
                                                <button
                                                    key={method.id}
                                                    onClick={() => setPaymentMethod(method.id)}
                                                    className={`p-4 border-2 rounded-xl text-left transition-all ${paymentMethod === method.id
                                                        ? "border-red-500 bg-red-50"
                                                        : "border-gray-200 hover:border-gray-300"
                                                        }`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className={`p-2 rounded-lg bg-${method.color}-100`}>
                                                            <Icon className={`w-5 h-5 text-${method.color}-600`} />
                                                        </div>
                                                        <span className="font-medium text-gray-900">{method.name}</span>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Payment Details */}
                                {paymentMethod && (
                                    <div className="bg-white rounded-xl shadow-sm p-6 animate-fade-in">
                                        {/* PromptPay / QR Code */}
                                        {paymentMethod === "promptpay" && (
                                            <div className="text-center">
                                                <h3 className="font-semibold text-gray-900 mb-4">สแกน QR Code เพื่อชำระเงิน</h3>

                                                {/* Fake QR Code */}
                                                <div className="bg-white border-2 border-gray-200 rounded-xl p-4 inline-block mb-4">
                                                    <div className="w-48 h-48 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center">
                                                        <QrCode className="w-32 h-32 text-white" />
                                                    </div>
                                                </div>

                                                <div className="flex items-center justify-center gap-2 text-orange-600 mb-4">
                                                    <Clock size={18} />
                                                    <span className="font-mono font-bold">{formatTime(qrCountdown)}</span>
                                                </div>

                                                <p className="text-2xl font-bold text-gray-900 mb-2">
                                                    ฿{calculateTotal().toLocaleString()}
                                                </p>
                                                <p className="text-gray-500 text-sm">รองรับทุกธนาคาร Mobile Banking และ E-Wallet</p>
                                            </div>
                                        )}

                                        {/* Credit Card */}
                                        {paymentMethod === "credit" && (
                                            <div>
                                                <h3 className="font-semibold text-gray-900 mb-4">กรอกข้อมูลบัตร</h3>

                                                <div className="space-y-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-2">หมายเลขบัตร</label>
                                                        <input
                                                            type="text"
                                                            placeholder="1234 5678 9012 3456"
                                                            maxLength={19}
                                                            value={cardNumber}
                                                            onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, '').replace(/(.{4})/g, '$1 ').trim())}
                                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                                        />
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">วันหมดอายุ</label>
                                                            <input
                                                                type="text"
                                                                placeholder="MM/YY"
                                                                maxLength={5}
                                                                value={cardExpiry}
                                                                onChange={(e) => {
                                                                    let val = e.target.value.replace(/\D/g, '');
                                                                    if (val.length >= 2) val = val.slice(0, 2) + '/' + val.slice(2);
                                                                    setCardExpiry(val);
                                                                }}
                                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">CVV</label>
                                                            <input
                                                                type="password"
                                                                placeholder="•••"
                                                                maxLength={3}
                                                                value={cardCvv}
                                                                onChange={(e) => setCardCvv(e.target.value.replace(/\D/g, ''))}
                                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-2 mt-4 p-3 bg-green-50 rounded-lg text-green-700 text-sm">
                                                    <Shield size={18} />
                                                    <span>ข้อมูลของคุณจะถูกเข้ารหัสและเก็บรักษาอย่างปลอดภัย</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Mobile Banking */}
                                        {paymentMethod === "mobile" && (
                                            <div className="text-center">
                                                <h3 className="font-semibold text-gray-900 mb-4">เลือกธนาคาร</h3>

                                                <div className="grid grid-cols-3 gap-3 mb-6">
                                                    {["K PLUS", "SCB Easy", "Krungthai NEXT", "KMA", "ttb touch", "อื่นๆ"].map((bank) => (
                                                        <button
                                                            key={bank}
                                                            className="p-4 border-2 border-gray-200 rounded-xl hover:border-red-500 hover:bg-red-50 transition-all text-sm font-medium"
                                                        >
                                                            {bank}
                                                        </button>
                                                    ))}
                                                </div>

                                                <p className="text-gray-500 text-sm">ระบบจะนำคุณไปยังแอป Mobile Banking</p>
                                            </div>
                                        )}

                                        {/* Bank Transfer */}
                                        {paymentMethod === "transfer" && (
                                            <div>
                                                <h3 className="font-semibold text-gray-900 mb-4">โอนเงินผ่านบัญชีธนาคาร</h3>

                                                <div className="bg-gray-50 rounded-xl p-4 space-y-3 mb-4">
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">ชื่อบัญชี</span>
                                                        <span className="font-medium">บริษัท ไอที ช็อป จำกัด</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600">ธนาคาร</span>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-mono font-medium">123-4-56789-0</span>
                                                            <button
                                                                onClick={() => copyToClipboard("1234567890")}
                                                                className="p-1 hover:bg-gray-200 rounded transition-colors"
                                                            >
                                                                <Copy size={16} className={copied ? "text-green-600" : "text-gray-400"} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">ธนาคาร</span>
                                                        <span className="font-medium">กสิกรไทย</span>
                                                    </div>
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-600">ยอดชำระ</span>
                                                        <span className="font-bold text-red-600">฿{calculateTotal().toLocaleString()}</span>
                                                    </div>
                                                </div>

                                                <p className="text-gray-500 text-sm text-center">
                                                    กรุณาแนบสลิปการโอนเงินเพื่อยืนยันการชำระเงิน
                                                </p>
                                            </div>
                                        )}

                                        {/* TrueMoney Wallet */}
                                        {paymentMethod === "wallet" && (
                                            <div className="text-center">
                                                <h3 className="font-semibold text-gray-900 mb-4">ชำระด้วย TrueMoney Wallet</h3>

                                                <div className="bg-orange-50 rounded-xl p-6 mb-4">
                                                    <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                                        <Wallet className="w-8 h-8 text-white" />
                                                    </div>
                                                    <p className="text-2xl font-bold text-gray-900 mb-2">
                                                        ฿{calculateTotal().toLocaleString()}
                                                    </p>
                                                    <p className="text-gray-600 text-sm">ระบบจะหักเงินจากกระเป๋า TrueMoney Wallet</p>
                                                </div>

                                                <button className="w-full py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-all">
                                                    เปิด TrueMoney Wallet
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Confirm Button */}
                                {paymentMethod && (
                                    <button
                                        onClick={handleConfirmPayment}
                                        disabled={processing}
                                        className="w-full py-4 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {processing ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                กำลังดำเนินการ...
                                            </>
                                        ) : (
                                            <>
                                                <Check size={20} />
                                                ชำระเงินจำนวน ฿{calculateTotal().toLocaleString()}
                                            </>
                                        )}
                                    </button>
                                )}
                            </>
                        )}
                    </div>

                    {/* Order Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24">
                            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                <ShoppingBag className="w-5 h-5 text-red-600" />
                                สรุปคำสั่งซื้อ
                            </h3>

                            <div className="space-y-3 mb-4">
                                {cartItems.map((item) => (
                                    <div key={item.orderItemId} className="flex gap-3">
                                        <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                                            <img
                                                src={item.ImageUrl ? `${API_BASE_URL}${item.ImageUrl}` : "https://via.placeholder.com/150?text=No+Image"}
                                                alt={item.productName}
                                                className="w-full h-full object-cover"
                                                onError={(e) => { e.target.src = "https://via.placeholder.com/150?text=No+Image"; }}
                                            />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">{item.productName}</p>
                                            <p className="text-sm text-gray-500">x{item.quantity}</p>
                                            <p className="text-sm font-medium text-red-600">
                                                ฿{(item.price * item.quantity).toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="border-t border-gray-200 pt-4 space-y-2">
                                {/* Discount Code */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">โค้ดส่วนลด</label>
                                    {!discountApplied ? (
                                        <div className="flex flex-col gap-2">
                                            <input
                                                type="text"
                                                placeholder="กรอกโค้ดส่วนลด"
                                                value={discountCode}
                                                onChange={(e) => setDiscountCode(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                            />
                                            <button
                                                onClick={applyDiscountCode}
                                                disabled={!discountCode.trim()}
                                                className="w-full py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                ใช้โค้ด
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-green-700 font-medium text-sm">✓ {discountCode.toUpperCase()}</p>
                                                    <p className="text-green-600 text-xs">ส่วนลด {discountPercent}%</p>
                                                </div>
                                                <button
                                                    onClick={removeDiscount}
                                                    className="text-red-500 hover:text-red-700 text-xs font-medium"
                                                >
                                                    ยกเลิก
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">รวมค่าสินค้า</span>
                                    <span>฿{calculateSubtotal().toLocaleString()}</span>
                                </div>
                                {discountApplied && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-green-600">ส่วนลด ({discountPercent}%)</span>
                                        <span className="text-green-600">-฿{calculateDiscount().toLocaleString()}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">ค่าจัดส่ง</span>
                                    <span className="text-green-600">ฟรี</span>
                                </div>
                                <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-200">
                                    <span>ยอดสุทธิ</span>
                                    <span className="text-red-600">฿{calculateTotal().toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
      `}</style>
        </div>
    );
};

export default Checkout;
